"use client";
import { motion } from "motion/react";
import {
  Hotel,
  Sparkles,
  DoorClosed,
  Utensils,
  Calendar,
  TrendingDown,
} from "lucide-react";

interface IRoompricingdetails {
  RoomCategory: string;
  NoOfRooms: number | null;
  Meal: string;
  OneNightRate: number | null;
  OneNightRateType: string | null;
  TwoNightRate: number | null;
  TwoNightRateType: string | null;
}

interface RoomPricingSectionProps {
  roomPricingDetails: IRoompricingdetails[];
  formatCurrency: (amount: number) => string;
}

export default function RoomPricingSection({
  roomPricingDetails,
  formatCurrency,
}: RoomPricingSectionProps) {
  if (!roomPricingDetails || roomPricingDetails.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="relative"
    >
      {/* Header with Gradient */}
      <div className="relative mb-10">
        <div className="absolute inset-0 bg-gradient-to-r from-[#B42025]/10 to-[#C9A962]/10 rounded-3xl blur-3xl" />
        <div className="relative bg-gradient-to-r from-[#1A1A1A] to-[#2A2A2A] rounded-3xl p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative">
                <div className="p-3 bg-gradient-to-br from-[#B42025] to-[#C9A962] rounded-2xl">
                  <Hotel className="h-8 w-8 text-white" />
                </div>
                <div className="absolute -top-2 -right-2">
                  <Sparkles className="h-5 w-5 text-[#C9A962] animate-pulse" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl md:text-4xl font-bold text-white mb-2">
                  Room Categories & Pricing
                </h2>
                <p className="text-gray-300">
                  Choose your perfect stay from our exclusive collection
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                <span className="text-white text-sm font-medium">
                  {roomPricingDetails.length} Options
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Cards Grid - Mobile Friendly */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
        {roomPricingDetails.map((room, index) => {
          const oneNightRate = room?.OneNightRate || 0;
          const twoNightRate = room?.TwoNightRate || 0;
          const hasTwoNightRate = twoNightRate > 0;
          const savings = hasTwoNightRate ? oneNightRate * 2 - twoNightRate : 0;

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={{ y: -4 }}
              className="group"
            >
              <div className="relative h-full bg-white rounded-2xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="p-6">
                  {/* Room Category Header */}
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#B42025] transition-colors">
                      {room?.RoomCategory}
                    </h3>
                    <div className="h-1 w-8 bg-gradient-to-r from-[#B42025] to-[#C9A962] rounded-full mt-2" />
                  </div>

                  {/* Quick Info */}
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <DoorClosed className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-700">
                        {room?.NoOfRooms || 0} rooms
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Utensils className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-700">
                        {room?.Meal}
                      </span>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="space-y-4">
                    {/* 1 Night Rate */}
                    {oneNightRate > 0 && (
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm text-gray-600">1 Night</div>
                            <div className="text-lg font-bold text-[#1A1A1A]">
                              <span>*</span>
                              {formatCurrency(oneNightRate)}
                              <span className="text-xs text-gray-500">
                                {" + Tax"}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              {room?.OneNightRateType || "Per Room"}
                            </div>
                          </div>
                          <Calendar className="h-8 w-8 text-gray-400" />
                        </div>
                      </div>
                    )}

                    {/* 2 Nights Rate with Savings */}
                    {hasTwoNightRate && (
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="relative p-4 bg-gradient-to-br from-[#F9F7F4] to-white rounded-xl border-2 border-[#C9A962]/20"
                      >
                        {/* Savings Badge */}
                        {savings > 0 && (
                          <div className="absolute -top-2 right-4">
                            <div className="px-2 py-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full">
                              <span className="text-xs text-white font-semibold flex items-center gap-1">
                                <TrendingDown className="h-3 w-3" />
                                Save {formatCurrency(savings)}
                              </span>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm text-gray-600">
                              2 Nights
                            </div>
                            <div className="text-xl font-bold text-[#B42025] flex items-baseline flex-wrap">
                              <span>*</span>
                              {formatCurrency(twoNightRate)}
                              <span className="text-xs text-gray-500">
                                {` + Tax`}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              {room?.TwoNightRateType || "Per Room"}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-700">
                              {savings > 0 && (
                                <span className="line-through text-gray-500">
                                  <span>*</span>
                                  {formatCurrency(oneNightRate * 2)}
                                </span>
                              )}
                            </div>
                            {savings > 0 && (
                              <div className="text-xs text-green-600 font-semibold">
                                Better value!
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Per Night Average */}
                    {hasTwoNightRate && (
                      <div className="text-center text-sm text-gray-600">
                        <span className="text-[#C9A962] font-semibold">
                          <span>*</span>
                          {formatCurrency(twoNightRate / 2)}
                        </span>{" "}
                        per night average
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}
